// ============================================================
// govemCompensationEngine.js
// เครื่องคำนวณการเลื่อนค่าตอบแทนพนักงานราชการ (pure functions, ไม่ผูก Vue)
//
// พอร์ตจาก college/gov_em/cal_compensation.vue (buildRecord → calculateCompensation
// → applyRoundingAndBudgetControl) ให้ "หลักการเดียวกับสถานศึกษา" ทุกขั้นตอน
// ใช้โดยหน้าส่วนกลาง administrator/gov_em/central_compensation.vue
//
// ส่วนที่ต่างจากหน้าสถานศึกษาโดยตั้งใจ (ป้องกันข้อมูลผิดพลาด):
//   1) ค่าตอบแทน ≤ 0 → ไม่มีสิทธิ์ (หน้าเดิมให้มีสิทธิ์แล้วได้ % เป็น NaN)
//   2) ค่าตอบแทนเดิม ≥ ขั้นสูงกลุ่มงาน → เลื่อน 0 บาท + ถึงเพดาน
//      (หน้าเดิมคำนวณเงินเลื่อนติดลบ)
//   3) หลังขั้นกระจายวงเงินคงเหลือ ตัดรายที่เกิน maxPct% (ปัดขึ้นหลักสิบ) ข้อ 1(2)
//      (หน้าเดิมตรวจแค่ขั้นสูงกลุ่มงาน ทำให้สถานศึกษาที่มีผู้ไม่มีสิทธิ์มาก บางรายได้เกิน 6%)
//   4) เลือกรอบผลประเมินแบบ latest ได้ (รอบ 2 ถ้ามี ไม่มีใช้รอบ 1)
// ============================================================

// ขั้นสูงกลุ่มงาน ตามประกาศ พ.ศ. 2568 (แนบท้าย) — ต้องตรงกับหน้าสถานศึกษา
export const GROUP_MAX_SALARY = {
  บริการ: 20210,
  เทคนิคทั่วไป: 24930,
  เทคนิคพิเศษ: 59790,
  บริหารทั่วไป: 34700,
  วิชาชีพเฉพาะ: 44550,
  เชี่ยวชาญเฉพาะ: 68350
};
const GROUP_KEY_MAP = {
  บริการ: "บริการ",
  เทคนิค: "เทคนิคทั่วไป",
  เทคนิคทั่วไป: "เทคนิคทั่วไป",
  เทคนิคพิเศษ: "เทคนิคพิเศษ",
  บริหารทั่วไป: "บริหารทั่วไป",
  วิชาชีพเฉพาะ: "วิชาชีพเฉพาะ",
  เชี่ยวชาญเฉพาะ: "เชี่ยวชาญเฉพาะ"
};

export const DEFAULT_PARAMS = {
  budgetPct: 4.0, // วงเงินรวม ≤ 4% ข้อ 1(3)
  maxPct: 6.0, // สูงสุดรายบุคคล ≤ 6% ข้อ 1(2)
  roundMode: "average" // "average" (ตามระเบียบ คพร.) | "1" | "2" | "latest" (ใช้เปรียบเทียบเท่านั้น)
};

// ปัดเศษ: เศษไม่ถึง 10 บาท → ปรับขึ้น 10 บาท (ข้อ 1(6))
export function roundUpTen(val) {
  const r = val % 10;
  return r === 0 ? val : val - r + 10;
}

/**
 * วันลาที่นับตามข้อ 4(8)(ข) / 1(8): ลาป่วย + ลากิจส่วนตัว นับเฉพาะวันทำการ (+ ลาคลอดส่วนที่เกิน 90 วัน)
 * ใช้ช่อง *_leave_workdays ถ้าไม่ได้กรอกวันทำการ (เป็น 0/ว่าง แต่มีจำนวนวัน) ใช้จำนวนวันแทนและแจ้งให้ตรวจข้อมูล
 * คืน { counted, sick, personal, excessMaternity, fallback: ['ลาป่วย', ...] }
 */
export function countedLeaveWorkdays(emp) {
  const fallback = [];
  const pick = (kind, label) => {
    const wd = parseFloat(emp[kind + "_leave_workdays"] || 0);
    const d = parseFloat(emp[kind + "_leave_days"] || 0);
    if (wd > 0 || d <= 0) return wd;
    fallback.push(label);
    return d;
  };
  const sick = pick("sick", "ลาป่วย");
  const personal = pick("personal", "ลากิจ");
  const excessMaternity = Math.max(0, parseFloat(emp.maternity_leave_days || 0) - 90);
  return { counted: sick + personal + excessMaternity, sick, personal, excessMaternity, fallback };
}

export function getGroupMax(groupName) {
  return GROUP_MAX_SALARY[GROUP_KEY_MAP[groupName] || "บริหารทั่วไป"] || 34700;
}

export function gradeLabel(score) {
  if (!score || score <= 0) return "ไม่มีข้อมูล";
  if (score >= 90) return "ดีเด่น";
  if (score >= 80) return "ดีมาก";
  if (score >= 70) return "ดี";
  if (score >= 60) return "พอใช้";
  return "ต้องปรับปรุง";
}

/** ชื่อโหมดรอบผลประเมินของรอบประมวลผล */
export function roundModeLabel(v) {
  return { average: "เฉลี่ย 2 รอบ (ระเบียบ คพร.)", latest: "รอบล่าสุด", 1: "รอบที่ 1", 2: "รอบที่ 2" }[v] || v;
}

/** ที่มาของฐานคะแนนจากแถวผลที่บันทึก เช่น "ร.1 85.5 + ร.2 90 → เฉลี่ย 87.75" / "ร.1 85.5 (มีรอบเดียว)" */
export function scoreSourceText(row) {
  const f = v => (Number(v) > 0 ? String(+Number(v).toFixed(3)) : null);
  const r1 = f(row.score_r1), r2 = f(row.score_r2);
  if (String(row.score_round) === "1+2") return `ร.1 ${r1} + ร.2 ${r2} → เฉลี่ย ${f(row.performance_score)}`;
  if (!(Number(row.performance_score) > 0)) return "ไม่มีผลประเมิน";
  return `ร.${row.score_round} ${f(row.performance_score)}` + (r1 && r2 ? "" : " (มีรอบเดียว)");
}

/**
 * เลือกคะแนนตามรอบ คืน { score, round, status, rounds[], s1, s2 }
 * "average" (ตามระเบียบ คพร.): ใช้ค่าเฉลี่ยผลประเมินทั้ง 2 รอบของปีงบประมาณเป็นฐานคะแนน
 *   มีครบ 2 รอบ → (รอบ 1 + รอบ 2) ÷ 2 (ไม่ปัด — ทศนิยมไม่เกิน 3 ตำแหน่ง)
 *   มีเพียงรอบเดียว → ใช้รอบนั้น แต่ต้องปฏิบัติงานในปีงบประมาณไม่น้อยกว่า 8 เดือน (ตรวจใน eligibilityChecks)
 * status รวม = approved เมื่อทุกรอบที่ใช้ได้รับอนุมัติ ไม่เช่นนั้นเป็นสถานะของรอบที่ยังไม่อนุมัติ
 */
export function pickScore(emp, roundMode) {
  const s1 = parseFloat(emp.score_r1 || 0);
  const s2 = parseFloat(emp.score_r2 || 0);
  const one = r => {
    const s = r === 1 ? s1 : s2;
    return { score: s, round: s > 0 ? r : null, status: emp["status_r" + r] || null, rounds: s > 0 ? [r] : [], s1, s2 };
  };
  if (roundMode === "1") return one(1);
  if (roundMode === "2") return one(2);
  if (roundMode === "average") {
    const used = [1, 2].filter(r => (r === 1 ? s1 : s2) > 0);
    if (used.length < 2) return used.length ? one(used[0]) : one(1);
    const sts = used.map(r => emp["status_r" + r] || null);
    return {
      score: Math.round(((s1 + s2) / 2) * 1000) / 1000, // ตัดเศษ floating point เท่านั้น ค่าเฉลี่ยจริงมีทศนิยม ≤ 3 ตำแหน่ง
      round: "1+2",
      status: sts.every(s => s === "approved") ? "approved" : sts.find(s => s !== "approved") || null,
      rounds: used,
      s1,
      s2
    };
  }
  return s2 > 0 ? one(2) : one(1); // latest
}

/**
 * สร้าง record + ตรวจสิทธิ์ (เทียบ buildRecord ของหน้าสถานศึกษา)
 * emp = แถวจาก govem_compensation_central.php?action=college_data
 */
export function buildRecord(emp, params, order) {
  const p = { ...DEFAULT_PARAMS, ...params };
  const salary = parseFloat(emp.salary_s || 0);
  const { score, round, status } = pickScore(emp, p.roundMode);
  const hasAttendance = emp.att_id !== null && emp.att_id !== undefined;

  const personalTimes = parseInt(emp.personal_leave_times || 0);
  const lateCount = parseInt(emp.late_count || 0);
  const absentCount = parseInt(emp.absent_count || 0);

  // วันลาที่นับตามข้อ 1(8): ลาป่วย + ลากิจ (นับเฉพาะวันทำการ) + ลาคลอดส่วนเกิน 90 วัน
  // (ลาราชการทหาร/ลาพักผ่อน ไม่นับ)
  const totalCountedLeave = countedLeaveWorkdays(emp).counted;

  const groupMax = getGroupMax(emp.name_type_group || "บริหารทั่วไป");
  let dataIssue = "";
  if (salary <= 0) dataIssue = "ไม่มีข้อมูลค่าตอบแทน";
  else if (salary >= groupMax)
    dataIssue = `ค่าตอบแทนเดิม ${salary} ≥ ขั้นสูงกลุ่มงาน ${groupMax}`;

  let eligible = true;
  let ineligible_reason = "";
  const roundText = p.roundMode === "latest" ? "รอบ 1/2" : p.roundMode === "average" ? "เฉลี่ย 2 รอบ" : "รอบ " + p.roundMode;

  if (emp.base_status && emp.base_status !== "active") {
    // เกษียณ/ลาออกก่อน 1 ต.ค. — นับเป็นวงเงิน แต่ไม่ได้รับการเลื่อน (govem_comp_roster.php)
    eligible = false;
    ineligible_reason = emp.base_note || "ไม่ได้ปฏิบัติงาน ณ 1 ต.ค.";
  } else if (salary <= 0) {
    eligible = false;
    ineligible_reason = "ไม่มีข้อมูลค่าตอบแทน (salary = 0)";
  } else if (!score || score <= 0) {
    eligible = false;
    ineligible_reason = "ยังไม่มีผลการประเมิน (" + roundText + ")";
  } else if (score < 60) {
    eligible = false;
    ineligible_reason = `คะแนน ${score} ต่ำกว่าระดับดี (< 60) ข้อ 1(2)`;
  } else if (absentCount > 0) {
    eligible = false;
    ineligible_reason = `ขาดราชการ ${absentCount} ครั้ง ข้อ 1(7)`;
  } else if (totalCountedLeave > 45) {
    eligible = false;
    ineligible_reason = `วันลา ${totalCountedLeave} วันทำการ เกิน 45 วันทำการ ข้อ 1(8)`;
  } else if (personalTimes > 14) {
    eligible = false;
    ineligible_reason = `ลากิจ ${personalTimes} ครั้ง เกิน 14 ครั้ง ข้อ 1(9)`;
  } else if (lateCount > 18) {
    eligible = false;
    ineligible_reason = `มาสาย ${lateCount} ครั้ง เกิน 18 ครั้ง ข้อ 1(9)`;
  }

  return {
    order,
    id_card: String(emp.id_card || ""),
    college_code: emp.college_code || "",
    full_name: `${emp.title_s || ""}${emp.frist_name || ""} ${emp.last_name ||
      ""}`.trim(),
    name_postion: emp.name_postion || "",
    name_type_group: emp.name_type_group || "บริหารทั่วไป",
    salary_s: salary,
    performance_score: score,
    score_round: round,
    assessment_status: status,
    has_attendance: hasAttendance,
    total_counted_leave: totalCountedLeave,
    personal_leave_times: personalTimes,
    late_count: lateCount,
    absent_count: absentCount,
    data_issue: dataIssue,
    eligible,
    ineligible_reason,
    max_increase_6_percent: 0,
    individual_percentage_pre_manage: 0,
    initial_increase_pre_manage: 0,
    initial_rounded_pre_manage: 0,
    management_percentage: 0,
    actual_percentage_final: 0,
    actual_increase_amount: 0,
    actual_pct_raw: 0, // % เลื่อนจริงก่อนปัดเศษ
    actual_increase_raw: 0, // เลื่อนจริงก่อนปัดเศษ (บาท)
    final_rounded: 0,
    new_salary: salary,
    capped: false,
    pct_capped: false
  };
}

// ปัดเศษ + ควบคุมวงเงิน + กระจายเงินที่เหลือจากคนถึงเพดาน (เทียบ applyRoundingAndBudgetControl)
function applyRoundingAndBudgetControl(eligible, totalBudget, maxPct) {
  const MAX_ITER = 20;
  const budgetPerMonth = totalBudget / 12;

  const calcFinal = (emp, pct) => {
    const rawInc = emp.salary_s * (pct / 100);
    let inc = roundUpTen(rawInc);
    let newSal = emp.salary_s + inc;
    let capped = false;
    const maxSal = getGroupMax(emp.name_type_group);
    if (newSal > maxSal) {
      inc = roundUpTen(Math.max(0, maxSal - emp.salary_s));
      // ไม่ให้ติดลบเมื่อค่าตอบแทนเดิมเกินขั้นสูงอยู่แล้ว (ส่วนต่างจากหน้าสถานศึกษา)
      inc = Math.max(0, Math.min(inc, maxSal - emp.salary_s));
      newSal = emp.salary_s + inc;
      capped = true;
    }
    return { inc, newSal, capped };
  };

  eligible.forEach(emp => {
    const r = calcFinal(emp, emp.actual_percentage_final);
    emp.final_rounded = r.inc;
    emp.new_salary = r.newSal;
    emp.capped = r.capped;
  });

  for (let iter = 0; iter < MAX_ITER; iter++) {
    const usedMo = eligible.reduce((s, e) => s + e.final_rounded, 0);
    const remainMo = budgetPerMonth - usedMo;
    if (remainMo < 10) break;
    const nonCapped = eligible.filter(e => !e.capped);
    if (nonCapped.length === 0) break;

    const nonCappedSalary = nonCapped.reduce((s, e) => s + e.salary_s, 0);
    let redistributed = 0;

    nonCapped.forEach(emp => {
      const addMo = remainMo * (emp.salary_s / nonCappedSalary);
      const addRounded = roundUpTen(addMo);
      const tryInc = emp.final_rounded + addRounded;
      const tryNewSal = emp.salary_s + tryInc;
      const maxSal = getGroupMax(emp.name_type_group);

      if (tryNewSal <= maxSal) {
        emp.final_rounded = tryInc;
        emp.new_salary = tryNewSal;
        redistributed += addRounded;
      } else {
        const capInc = Math.max(0, maxSal - emp.salary_s);
        redistributed += capInc - emp.final_rounded;
        emp.final_rounded = capInc;
        emp.new_salary = emp.salary_s + capInc;
        emp.capped = true;
      }
    });

    if (redistributed <= 0) break;
  }

  // ยังเกินวงเงิน → ลดรายที่ได้มากสุดทีละ 10 บาท (ข้อ 1(5))
  let totalYearFinal = eligible.reduce((s, e) => s + e.final_rounded * 12, 0);
  let cutIter = 0;
  while (totalYearFinal > totalBudget && cutIter < 500) {
    const maxEmp = eligible
      .filter(e => !e.capped && e.final_rounded >= 10)
      .reduce((m, c) => (c.final_rounded > m.final_rounded ? c : m), {
        final_rounded: -1
      });
    if (maxEmp.final_rounded < 0) break;
    maxEmp.final_rounded -= 10;
    maxEmp.new_salary -= 10;
    totalYearFinal -= 120;
    cutIter++;
  }

  // เพดานรายบุคคล maxPct% ปัดขึ้นหลักสิบ ข้อ 1(2) — หน้าสถานศึกษาไม่ได้ตรวจหลังขั้นกระจาย
  // ตัดส่วนเกินออกโดยไม่นำไปกระจายต่อ (วงเงินส่วนนั้นไม่ถูกใช้)
  eligible.forEach(emp => {
    const pctCapInc = roundUpTen(emp.salary_s * (maxPct / 100));
    if (emp.final_rounded > pctCapInc) {
      emp.final_rounded = pctCapInc;
      emp.new_salary = emp.salary_s + pctCapInc;
      emp.pct_capped = true;
    }
  });
}

/**
 * คำนวณทั้งสถานศึกษา (เทียบ calcTotalSalary + calculateCompensation)
 * @param {Array} employees แถวดิบจาก API ของสถานศึกษาเดียว
 * @param {Object} params { budgetPct, maxPct, roundMode }
 * @returns {{ records: Array, summary: Object }}
 */
export function calculateCollege(employees, params) {
  const p = { ...DEFAULT_PARAMS, ...params };
  const records = (employees || []).map((emp, i) => buildRecord(emp, p, i + 1));

  const totalSalary = records.reduce((s, e) => s + e.salary_s, 0);
  // วงเงิน = % × ฐาน × 12 เดือน (ข้อ 1(3))
  const totalBudget = totalSalary * (p.budgetPct / 100) * 12;

  const eligible = records.filter(e => e.eligible);
  const totalScore = eligible.reduce((s, e) => s + e.performance_score, 0);

  if (eligible.length > 0 && totalScore > 0) {
    // ขั้นที่ 1-4: ขั้นต้น
    eligible.forEach(emp => {
      emp.max_increase_6_percent = parseFloat(
        (emp.salary_s * (p.maxPct / 100)).toFixed(2)
      );
      emp.individual_percentage_pre_manage = parseFloat(
        ((p.maxPct * emp.performance_score) / totalScore).toFixed(3)
      );
      emp.initial_increase_pre_manage = parseFloat(
        (emp.salary_s * (emp.individual_percentage_pre_manage / 100)).toFixed(2)
      );
      emp.initial_rounded_pre_manage = roundUpTen(
        emp.initial_increase_pre_manage
      );
    });

    // ขั้นที่ 5-6: วงเงินคงเหลือ → ร้อยละบริหาร (เท่ากันทุกคน)
    const sumInitialRounded = eligible.reduce(
      (s, e) => s + e.initial_rounded_pre_manage,
      0
    );
    const remainingBudgetForMgmt = totalBudget - sumInitialRounded * 12;
    const eligibleSalarySum = eligible.reduce((s, e) => s + e.salary_s, 0);
    const mgmtPctPerPerson = parseFloat(
      ((remainingBudgetForMgmt / 12 / eligibleSalarySum) * 100).toFixed(3)
    );

    // ขั้นที่ 7-8
    eligible.forEach(emp => {
      emp.management_percentage = mgmtPctPerPerson;
      const rawActualPct =
        emp.individual_percentage_pre_manage + emp.management_percentage;
      emp.actual_percentage_final = parseFloat(
        Math.min(rawActualPct, p.maxPct).toFixed(3)
      );
      emp.actual_increase_amount = parseFloat(
        (emp.salary_s * (emp.actual_percentage_final / 100)).toFixed(2)
      );
      // เก็บค่าก่อนปัดเศษไว้ตรวจสอบ (ขั้นที่ 9 จะเขียนทับด้วยผลหลังปัด/กระจาย)
      emp.actual_pct_raw = emp.actual_percentage_final;
      emp.actual_increase_raw = emp.actual_increase_amount;
    });

    applyRoundingAndBudgetControl(eligible, totalBudget, p.maxPct);

    // ขั้นที่ 9: % จริงหลัง redistribute
    eligible.forEach(emp => {
      emp.actual_increase_amount = emp.final_rounded;
      emp.actual_percentage_final = parseFloat(
        ((emp.final_rounded / emp.salary_s) * 100).toFixed(3)
      );
    });
  }

  const distributedMonth = eligible.reduce((s, e) => s + e.final_rounded, 0);
  const summary = {
    personnel_count: records.length,
    eligible_count: eligible.length,
    ineligible_count: records.length - eligible.length,
    no_score_count: records.filter(e => !(e.performance_score > 0)).length,
    unapproved_score_count: records.filter(
      e => e.performance_score > 0 && e.assessment_status !== "approved"
    ).length,
    no_attendance_count: records.filter(e => !e.has_attendance).length,
    data_issue_count: records.filter(e => e.data_issue).length,
    capped_count: eligible.filter(e => e.capped).length,
    pct_capped_count: eligible.filter(e => e.pct_capped).length,
    total_salary: totalSalary,
    eligible_salary: eligible.reduce((s, e) => s + e.salary_s, 0),
    budget_year: parseFloat(totalBudget.toFixed(2)),
    distributed_month: distributedMonth,
    distributed_year: distributedMonth * 12,
    new_salary_total: records.reduce((s, e) => s + e.new_salary, 0)
  };

  return { records, summary };
}

// =====================================================================
// วิธี "ร้อยละร่วมตามคะแนน" (calc_method = equal_factor) — ตามข้อเสนอผู้ทรงคุณวุฒิ ต.ค. 2569
//
//   วงเงิน/เดือน = budgetPct% × ค่าตอบแทนรวมของทุกคนในฐาน (รวมผู้เกษียณ/ลาออกก่อน 1 ต.ค.)
//   ผู้มีสิทธิ์ทุกคนใช้ "อัตรากลาง" k เดียวกัน: ร้อยละ_i = min(maxPct, k × คะแนน_i / 100)
//   เงินเลื่อน_i = ปัดขึ้นหลักสิบ(ค่าตอบแทน_i × ร้อยละ_i) — ปัดขึ้นทุกคนด้วยกติกาเดียวกัน
//                  (เพดาน maxPct ปัดขึ้นหลักสิบ, ไม่เกินขั้นสูงกลุ่มงาน)
//   k = ค่ามากที่สุดที่ผลรวมเงินเลื่อนไม่เกินวงเงิน → เงินพอ: ทุกคนได้ปัดขึ้น / เงินไม่พอ: ลด k ให้ทุกคนเท่ากัน
//   (แทนการตัดรายบุคคลทีละ 10 บาท หรือการลดคะแนนทุกคนด้วยมือแบบเดิม)
//   เงินที่เหลือมีคำอธิบายเสมอ: ทุกคนได้เต็มเพดานแล้ว / เพิ่มอัตราขั้นถัดไปต้องใช้วงเงินเกินที่เหลือ / ไม่มีผู้มีสิทธิ์
// =====================================================================

// คะแนนขั้นต่ำ 75 = ระดับดี (ประกาศ คพร. ข้อ 11 ผลการประเมินไม่ต่ำกว่าระดับดี)
export const V2_DEFAULTS = { minScore: 75, minMonths: 8 };

function num(v) {
  return Number(v || 0);
}
function fmtTh(v, d = 0) {
  return Number(v).toLocaleString("th-TH", { minimumFractionDigits: d, maximumFractionDigits: d });
}
function roundUp10Safe(x) {
  // กัน floating point เช่น 700.0000000001 → 710
  return Math.ceil(x / 10 - 1e-9) * 10;
}
/**
 * วันทำการแรกของเดือนกุมภาพันธ์ ค.ศ. y (ข้ามเสาร์-อาทิตย์ และวันหยุดราชการใน holidays['YYYY-MM-DD'])
 * ต้องตรงกับ govemFebFirstWorkday ของ govem_comp_docs_helpers.php
 */
export function febFirstWorkday(y, holidays = []) {
  const set = new Set(holidays || []);
  for (let d = 1; d <= 28; d++) {
    const iso = `${y}-02-${String(d).padStart(2, "0")}`;
    const wd = new Date(Date.UTC(y, 1, d)).getUTCDay();
    if (wd !== 0 && wd !== 6 && !set.has(iso)) return d;
  }
  return 1;
}

/**
 * อายุงานในปีงบประมาณ Y (1 ต.ค. Y-1 ถึง 30 ก.ย. Y) จากวันบรรจุ ค.ศ. 'YYYY-MM-DD' คืน { months, febRounded }
 * นับเดือนเต็ม (ชนเดือน) — ต้องไม่น้อยกว่า 8 เดือน = เริ่มงานไม่หลัง 1 ก.พ.
 * ข้อยกเว้นตามแนวปฏิบัติสำนักงาน ก.พ.: 1 ก.พ. ตรงกับวันหยุดราชการ และเริ่มงานวันทำการแรกของเดือน ก.พ.
 * (เช่น 2 ก.พ.) ให้นับเป็น 8 เดือน (febRounded = true) — เริ่ม 2 ก.พ. ที่ 1 ก.พ. เป็นวันทำการ ไม่ได้รับยกเว้น
 */
export function serviceMonths(appointCe, compYear, holidays = []) {
  if (!appointCe) return { months: null, febRounded: false };
  const [y, m, d] = appointCe.split("-").map(Number);
  const fyStartY = compYear - 544; // ค.ศ. ของ 1 ต.ค. ต้นปีงบ
  let sy = y, sm = m, sd = d;
  if (y < fyStartY || (y === fyStartY && (m < 10 || (m === 10 && d <= 1)))) {
    sy = fyStartY; sm = 10; sd = 1;
  }
  // นับเดือนเต็มจากวันเริ่มถึง 30 ก.ย. (สิ้นปีงบ = 1 ต.ค. ปีถัดไป แบบไม่รวม)
  const endY = compYear - 543, endM = 10, endD = 1;
  let months = (endY - sy) * 12 + (endM - sm);
  if (endD < sd) months -= 1;
  months = Math.max(0, Math.min(12, months));
  const febRounded = sy === fyStartY + 1 && sm === 2 && sd > 1 && sd <= febFirstWorkday(sy, holidays);
  return { months: febRounded ? 8 : months, febRounded };
}
export function monthsInFiscalYear(appointCe, compYear, holidays = []) {
  return serviceMonths(appointCe, compYear, holidays).months;
}

/** ตรวจคุณสมบัติรายข้อ คืน { eligible, checks[], reason } */
export function eligibilityChecks(emp, p) {
  const checks = [];
  const add = (key, label, pass, detail, blocking = true) => checks.push({ key, label, pass, detail, blocking });
  const salary = num(emp.salary_s);
  const { score, round, status, rounds, s1, s2 } = pickScore(emp, p.roundMode);
  const groupMax = getGroupMax(emp.name_type_group || "บริหารทั่วไป");
  const { months, febRounded } = serviceMonths(emp.appoint_date || null, p.compYear, p.holidays);
  const monthsText = months === null
    ? "ไม่มีวันที่บรรจุ — ถือว่าผ่าน (ควรตรวจข้อมูล)"
    : `${months} เดือน (บรรจุ ${emp.appoint_date}` +
      (febRounded ? " — 1 ก.พ. เป็นวันหยุดราชการ เริ่มงานวันทำการแรกของเดือน อนุโลมนับเป็น 8 เดือน (แนวปฏิบัติสำนักงาน ก.พ.)" : "") + ")";
  const isAvg = p.roundMode === "average";
  let scoreText = "ไม่พบผลประเมินรอบที่ใช้";
  if (score > 0) {
    scoreText = isAvg && rounds.length === 2
      ? `รอบ 1 = ${s1}, รอบ 2 = ${s2} → เฉลี่ย ${score} คะแนน`
      : `${score} คะแนน (รอบ ${round}${isAvg ? " — มีผลประเมินรอบเดียว" : ""})`;
  }

  add("employed", "ปฏิบัติงาน ณ 1 ต.ค. (ไม่เกษียณ/ลาออกก่อน)", !emp.base_status || emp.base_status === "active",
    emp.base_note || "ปฏิบัติงานต่อเนื่อง");
  add("salary", "มีข้อมูลค่าตอบแทน ณ 1 ก.ย.", salary > 0, salary > 0 ? fmtTh(salary) + " บาท" : "ไม่มีข้อมูล");
  add("score", isAvg ? "มีผลการประเมิน (แบบ ปผ.พนร.) — ใช้ค่าเฉลี่ย 2 รอบ" : "มีผลการประเมิน (แบบ ปผ.พนร.)", score > 0, scoreText);
  if (score > 0) {
    add("min_score", `คะแนน${isAvg && rounds.length === 2 ? "เฉลี่ย" : ""}ไม่ต่ำกว่า ${p.minScore}`, score >= p.minScore, `${score} คะแนน`);
    add("approved", "ผลประเมินผ่านการอนุมัติ", status === "approved",
      status === "approved" ? "อนุมัติแล้ว" : `สถานะ: ${status || "-"}`, false);
  }
  if (isAvg && rounds.length === 1) {
    // มีผลประเมินเพียงรอบเดียว — ใช้ได้เมื่อปฏิบัติงานในปีงบประมาณไม่น้อยกว่า 8 เดือน
    add("single_round", `มีผลประเมินรอบเดียว ต้องปฏิบัติงานไม่น้อยกว่า ${p.minMonths} เดือน`,
      months === null || months >= p.minMonths,
      `ไม่มีผลรอบ ${rounds[0] === 1 ? 2 : 1} · อายุงาน ${monthsText}`);
  }
  add("months", `ปฏิบัติงานในปีงบประมาณไม่น้อยกว่า ${p.minMonths} เดือน`, months === null || months >= p.minMonths, monthsText);
  const hasAtt = emp.att_id !== null && emp.att_id !== undefined;
  const lv = countedLeaveWorkdays(emp);
  const counted = lv.counted;
  const attText = t => (hasAtt ? t : "ไม่มีข้อมูลการลา");
  add("absent", "ไม่ขาดราชการ ข้อ 1(7)", num(emp.absent_count) === 0, attText(`ขาด ${num(emp.absent_count)} ครั้ง`));
  add("leave", "วันลาป่วย+ลากิจไม่เกิน 45 วันทำการ ข้อ 1(8)", counted <= 45,
    attText(`ลาป่วย ${lv.sick} + ลากิจ ${lv.personal}${lv.excessMaternity ? " + ลาคลอดส่วนเกิน " + lv.excessMaternity : ""} = ${counted} วันทำการ`));
  if (lv.fallback.length) {
    add("leave_workdays", "มีข้อมูลวันทำการของวันลา", false,
      `ไม่ได้กรอกวันทำการของ${lv.fallback.join("/")} — ใช้จำนวนวันแทน (ควรให้หน่วยงานกรอกวันทำการ)`, false);
  }
  add("personal", "ลากิจไม่เกิน 14 ครั้ง ข้อ 1(9)", num(emp.personal_leave_times) <= 14, attText(`${num(emp.personal_leave_times)} ครั้ง`));
  add("late", "มาสายไม่เกิน 18 ครั้ง ข้อ 1(9)", num(emp.late_count) <= 18, attText(`${num(emp.late_count)} ครั้ง`));
  add("attendance_data", "มีข้อมูลการลา/สาย/ขาด", hasAtt, hasAtt ? "มีข้อมูล" : "ไม่มีข้อมูล — ถือว่าไม่มีวันลา", false);
  add("ceiling", "ค่าตอบแทนต่ำกว่าขั้นสูงกลุ่มงาน", salary < groupMax, `ขั้นสูง ${fmtTh(groupMax)} บาท`, false);

  const failed = checks.filter(c => c.blocking && !c.pass);
  return { eligible: failed.length === 0, checks, reason: failed.map(c => `${c.label}: ${c.detail}`).join(" · "),
    score, round, status, rounds, s1, s2, months };
}

/**
 * คำนวณทั้งหน่วยงานด้วยวิธีร้อยละร่วมตามคะแนน
 * rows = แถวจาก college_data (govem_comp_roster.php)
 * params = { budgetPct, maxPct, roundMode, minScore, minMonths, compYear, holidays[], overrides }
 *   overrides = { [id_card]: { pct, reason } } — ส่วนกลางกำหนดร้อยละรายคน (0–maxPct) ต้องมีเหตุผล
 *   ผู้ถูกกำหนดร้อยละ: เงินเลื่อน = ปัดขึ้นหลักสิบ(ค่าตอบแทน × ร้อยละที่กำหนด) ไม่เกินขั้นสูง
 *   ผู้อื่นใช้อัตรากลาง k จากวงเงินที่เหลือ (วงเงิน − ยอดของผู้ถูกกำหนด) · กำหนดได้เฉพาะผู้มีสิทธิ์
 */
export function calculateCollegeV2(rows, params) {
  const p = { ...DEFAULT_PARAMS, ...V2_DEFAULTS, ...params };
  const records = (rows || []).map((emp, i) => {
    const e = eligibilityChecks(emp, p);
    const salary = num(emp.salary_s);
    const groupMax = getGroupMax(emp.name_type_group || "บริหารทั่วไป");
    const hasAtt = emp.att_id !== null && emp.att_id !== undefined;
    // ที่มาผลประเมินทุกรอบที่ใช้ (rounds[]) — ฟิลด์ระดับบนเป็นของรอบล่าสุดที่ใช้ (รองรับหน้าจอเดิม)
    const roundRefs = (e.rounds || []).map(r => ({
      round: r, id: emp["assess_id_r" + r], college: emp["assess_college_r" + r], score: r === 1 ? e.s1 : e.s2,
      status: emp["status_r" + r] || null, approved: emp["assess_approved_r" + r], approver: emp["assess_approver_r" + r],
      assessor: emp["assess_assessor_r" + r], files: emp["assess_files_r" + r]
    }));
    const last = roundRefs[roundRefs.length - 1];
    const ref = last ? { ...last, status: e.status, rounds: roundRefs } : null;
    return {
      order: i + 1,
      id_card: String(emp.id_card || ""),
      college_code: emp.college_code || "",
      full_name: `${emp.title_s || ""}${emp.frist_name || ""} ${emp.last_name || ""}`.trim(),
      name_postion: emp.name_postion || "",
      name_type_group: emp.name_type_group || "บริหารทั่วไป",
      salary_s: salary,
      performance_score: e.score,
      score_round: e.round,
      score_r1: e.s1 > 0 ? e.s1 : null,
      score_r2: e.s2 > 0 ? e.s2 : null,
      service_months: e.months,
      assessment_status: e.status,
      assessment_ref: ref,
      has_attendance: hasAtt,
      total_counted_leave: countedLeaveWorkdays(emp).counted,
      personal_leave_times: num(emp.personal_leave_times),
      late_count: num(emp.late_count),
      absent_count: num(emp.absent_count),
      base_status: emp.base_status || "active",
      transfer_note: emp.transfer_note || null,
      salary_note: emp.salary_note || null,
      checks: e.checks,
      eligible: e.eligible,
      ineligible_reason: e.eligible ? "" : e.reason,
      data_issue: salary <= 0 ? "ไม่มีข้อมูลค่าตอบแทน" : salary >= groupMax ? `ค่าตอบแทนเดิม ${salary} ≥ ขั้นสูงกลุ่มงาน ${groupMax}` : "",
      group_max: groupMax,
      individual_percentage_pre_manage: 0,
      initial_increase_pre_manage: 0,
      initial_rounded_pre_manage: 0,
      management_percentage: 0,
      actual_pct_raw: 0,
      actual_increase_raw: 0,
      actual_percentage_final: 0,
      actual_increase_amount: 0,
      final_rounded: 0,
      new_salary: salary,
      capped: false,
      pct_capped: false,
      override_pct: null,
      override_reason: null,
      explain: e.eligible ? "" : "ไม่ได้รับการเลื่อน — " + e.reason
    };
  });

  const totalSalary = records.reduce((s, r) => s + r.salary_s, 0);
  const poolMonth = totalSalary * (p.budgetPct / 100);
  const eligible = records.filter(r => r.eligible);

  // ส่วนกลางกำหนดร้อยละรายคน (เฉพาะผู้มีสิทธิ์)
  const overrides = p.overrides || {};
  const overridden = eligible.filter(r => overrides[r.id_card] && overrides[r.id_card].pct !== null && overrides[r.id_card].pct !== undefined);
  overridden.forEach(r => {
    r.override_pct = Number(overrides[r.id_card].pct);
    r.override_reason = overrides[r.id_card].reason || "";
  });
  const autoGroup = eligible.filter(r => r.override_pct === null);
  const ignoredOverrides = Object.keys(overrides).filter(id => !overridden.some(r => r.id_card === id)).length;
  const overrideAmount = r => {
    const pct = Math.min(p.maxPct, Math.max(0, r.override_pct));
    let amt = roundUp10Safe((r.salary_s * pct) / 100);
    const room = Math.max(0, r.group_max - r.salary_s);
    const ceilingCapped = amt > room;
    if (ceilingCapped) amt = room;
    return { pct, want: pct, amt, pctCapped: pct >= p.maxPct - 1e-12, ceilingCapped };
  };
  const overrideTotal = overridden.reduce((s, r) => s + overrideAmount(r).amt, 0);
  const autoPool = poolMonth - overrideTotal;
  const overBudget = autoPool < -1e-6;

  // เงินเลื่อนของคนหนึ่งที่อัตรากลาง k
  const amountAt = (r, k) => {
    const want = (k * r.performance_score) / 100;
    const pctCapped = want >= p.maxPct - 1e-12;
    const pct = Math.min(p.maxPct, want);
    let amt = roundUp10Safe((r.salary_s * pct) / 100);
    const room = Math.max(0, r.group_max - r.salary_s);
    const ceilingCapped = amt > room;
    if (ceilingCapped) amt = room;
    return { pct, want, amt, pctCapped, ceilingCapped };
  };
  const totalAt = k => autoGroup.reduce((s, r) => s + amountAt(r, k).amt, 0);

  let k = 0;
  let nextNeed = null;
  let allCapped = false;
  if (autoGroup.length && !overBudget) {
    const minScore = Math.min(...autoGroup.map(r => r.performance_score));
    const kMax = (p.maxPct * 100) / minScore; // ทุกคนถึงเพดาน maxPct
    if (totalAt(kMax) <= autoPool + 1e-6) {
      k = kMax;
      allCapped = true;
    } else {
      let lo = 0;
      let hi = kMax;
      for (let i = 0; i < 80; i++) {
        const mid = (lo + hi) / 2;
        if (totalAt(mid) <= autoPool + 1e-6) lo = mid;
        else hi = mid;
      }
      k = lo;
      nextNeed = totalAt(hi) - totalAt(lo); // วงเงินที่ต้องใช้เพิ่มเพื่อขยับอัตรากลางขั้นถัดไป
    }
  }

  eligible.forEach(r => {
    const a = r.override_pct !== null ? overrideAmount(r) : amountAt(r, k);
    r.individual_percentage_pre_manage = parseFloat(a.want.toFixed(3));
    r.actual_pct_raw = parseFloat(a.pct.toFixed(3));
    r.actual_increase_raw = parseFloat(((r.salary_s * a.pct) / 100).toFixed(2));
    r.final_rounded = a.amt;
    r.actual_increase_amount = a.amt;
    r.new_salary = r.salary_s + a.amt;
    r.actual_percentage_final = r.salary_s ? parseFloat(((a.amt / r.salary_s) * 100).toFixed(3)) : 0;
    r.pct_capped = a.pctCapped && !a.ceilingCapped;
    r.capped = a.ceilingCapped;
    const base = r.override_pct !== null
      ? `ส่วนกลางกำหนดร้อยละ ${a.pct}% (เหตุผล: ${r.override_reason || "-"})`
      : `คะแนน ${r.performance_score} × อัตรากลาง ${k.toFixed(4)}% ÷ 100 = ${a.want.toFixed(3)}%`;
    const raw = (r.salary_s * a.pct) / 100;
    if (r.override_pct !== null && !a.ceilingCapped) {
      r.explain = `${base} × ${fmtTh(r.salary_s)} = ${fmtTh(raw, 2)} บาท → ` +
        (Math.abs(raw - a.amt) < 1e-9 ? `${fmtTh(a.amt)} บาท` : `ปัดขึ้นเป็น ${fmtTh(a.amt)} บาท (ข้อ 1(6))`);
    } else if (a.ceilingCapped) {
      r.explain = `${base} → ถึงขั้นสูงกลุ่มงาน ${fmtTh(r.group_max)} บาท เลื่อนได้เพียง ${fmtTh(a.amt)} บาท ข้อ 1(4)`;
    } else if (a.pctCapped) {
      r.explain = `${base} ถึงเพดาน ${p.maxPct}% → ${p.maxPct}% ของ ${fmtTh(r.salary_s)} = ${fmtTh(raw, 2)} บาท → ` +
        (Math.abs(raw - a.amt) < 1e-9 ? `${fmtTh(a.amt)} บาท` : `ปัดขึ้นเป็น ${fmtTh(a.amt)} บาท`);
    } else {
      r.explain = `${base} × ${fmtTh(r.salary_s)} = ${fmtTh(raw, 2)} บาท → ` +
        (Math.abs(raw - a.amt) < 1e-9 ? `ลงตัวหลักสิบ ${fmtTh(a.amt)} บาท` : `ปัดขึ้นเป็น ${fmtTh(a.amt)} บาท (ปัดขึ้นหลักสิบทุกคน ข้อ 1(6))`);
    }
  });

  const distributedMonth = eligible.reduce((s, r) => s + r.final_rounded, 0);
  const leftover = poolMonth - distributedMonth;
  let leftoverReason = null;
  if (!eligible.length) leftoverReason = "ไม่มีผู้มีสิทธิ์ได้รับการเลื่อน วงเงินทั้งหมดไม่ถูกใช้";
  else if (overBudget) leftoverReason = "การกำหนดร้อยละรายคนของส่วนกลางใช้วงเงินเกิน 4% — ต้องแก้ไขก่อนบันทึก";
  else if (!autoGroup.length) leftoverReason = "ผู้มีสิทธิ์ทุกคนถูกกำหนดร้อยละรายคนโดยส่วนกลาง เงินที่เหลือไม่ได้จัดสรรเพิ่ม";
  else if (allCapped) leftoverReason = `ผู้มีสิทธิ์ทุกคนได้เต็มเพดานแล้ว (${p.maxPct}% หรือขั้นสูงกลุ่มงาน) จึงจัดสรรเพิ่มไม่ได้`;
  else if (leftover < 0.005) leftoverReason = "ใช้วงเงินครบพอดี ไม่มีเงินเหลือ";
  else if (nextNeed !== null) {
    leftoverReason = `เป็นเศษจากการปัดขึ้นหลักสิบ: หากเพิ่มอัตรากลางขั้นถัดไปต้องใช้วงเงินเพิ่มอีก ${fmtTh(nextNeed)} บาท/เดือน ` +
      `แต่คงเหลือเพียง ${fmtTh(leftover, 2)} บาท/เดือน — คงอัตราเดียวกันให้ทุกคนเพื่อความเป็นธรรม`;
  }
  const notes = [];
  const scored = eligible.map(r => r.performance_score);
  if (scored.length > 1 && scored.every(s => s === scored[0])) {
    notes.push(`ผู้มีสิทธิ์ทุกคนมีคะแนนเท่ากัน (${scored[0]}) — ทุกคนได้ร้อยละเท่ากัน`);
  }
  const nonActive = records.filter(r => r.base_status !== "active");
  if (nonActive.length) {
    notes.push(`นับค่าตอบแทนของผู้เกษียณ/ลาออกก่อน 1 ต.ค. ${nonActive.length} คน ` +
      `(${fmtTh(nonActive.reduce((s, r) => s + r.salary_s, 0))} บาท/เดือน) เป็นวงเงิน แต่ไม่ได้รับการเลื่อน — ` +
      `วงเงินส่วนนี้เฉลี่ยให้ผู้มีสิทธิ์ภายใต้เพดาน ${p.maxPct}%`);
  }
  const transfers = records.filter(r => r.transfer_note);
  if (transfers.length) notes.push(`มีผู้ย้ายมีผล 1 ต.ค. ${transfers.length} คน คำนวณที่สังกัดนี้ (สังกัด ณ 30 ก.ย.)`);
  if (overridden.length) {
    notes.push(`ส่วนกลางกำหนดร้อยละรายคน ${overridden.length} คน ใช้วงเงิน ${fmtTh(overrideTotal)} บาท/เดือน — ` +
      `ผู้มีสิทธิ์อื่น ${autoGroup.length} คนใช้อัตรากลางจากวงเงินที่เหลือ ${fmtTh(Math.max(0, autoPool), 2)} บาท/เดือน`);
  }
  if (ignoredOverrides) notes.push(`มีการกำหนดร้อยละรายคน ${ignoredOverrides} รายการที่ไม่มีผล (ผู้นั้นไม่มีสิทธิ์/ไม่อยู่ในฐาน)`);
  if (overBudget) notes.push(`การกำหนดร้อยละรายคนรวม ${fmtTh(overrideTotal)} บาท เกินวงเงิน ${fmtTh(poolMonth, 2)} บาท/เดือน`);
  const unapproved = eligible.filter(r => r.assessment_status !== "approved").length;
  if (unapproved) notes.push(`ใช้ผลประเมินที่ยังไม่อนุมัติ ${unapproved} คน — ควรให้สถานศึกษาอนุมัติก่อนยืนยันผล`);

  const summary = {
    method: "equal_factor",
    over_budget: overBudget,
    override_count: overridden.length,
    factor_k: k, // ความละเอียดเต็ม — ส่วนกลางใช้ตรวจสอบซ้ำฝั่งเซิร์ฟเวอร์ได้ตรงทุกบาท
    pool_month: parseFloat(poolMonth.toFixed(2)),
    leftover_month: parseFloat(leftover.toFixed(2)),
    leftover_reason: leftoverReason,
    next_step_need: nextNeed,
    notes,
    personnel_count: records.length,
    eligible_count: eligible.length,
    ineligible_count: records.length - eligible.length,
    retired_count: records.filter(r => r.base_status === "retired").length,
    resigned_count: records.filter(r => r.base_status === "resigned").length,
    transfer_count: transfers.length,
    no_score_count: records.filter(r => !(r.performance_score > 0)).length,
    unapproved_score_count: records.filter(r => r.performance_score > 0 && r.assessment_status !== "approved").length,
    no_attendance_count: records.filter(r => !r.has_attendance).length,
    data_issue_count: records.filter(r => r.data_issue).length,
    capped_count: eligible.filter(r => r.capped).length,
    pct_capped_count: eligible.filter(r => r.pct_capped).length,
    total_salary: totalSalary,
    eligible_salary: eligible.reduce((s, r) => s + r.salary_s, 0),
    budget_year: parseFloat((poolMonth * 12).toFixed(2)),
    distributed_month: distributedMonth,
    distributed_year: distributedMonth * 12,
    new_salary_total: records.reduce((s, r) => s + r.new_salary, 0)
  };
  return { records, summary };
}
